import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";

import {
  AppointmentCard,
  CommonPatientDetails,
} from "../../../Components/Common Components/CommonComponents";

import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { Colors } from "../../../Styles/Colors";
import { ImagePaths } from "../../../Utility/ImagePaths";
import { renderIconButton } from "../../Doctor/DoctorNotes/Common/DocCommonComponent";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CancelIcon from "@mui/icons-material/Cancel";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";


const DocApprovalList = () => {
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterDate, setFilterDate] = useState(new Date());
  const [statusCount, setStatusCount] = useState({});
  const [selectedStatus, setSelectedStatus] = useState("");
  const [cancelDialog, setCancelDialog] = useState(null);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  /* -------------------------------- FETCH -------------------------------- */
  const fetchAppointments = useCallback(() => {
    setIsLoading(true);
    let dateKey = filterDate ? dayjs(filterDate).format("YYYY-MM-DD") : "";
    selectedStatus !== "Pending" ? dateKey : null
    const qs = `?date=${dateKey}&status=${selectedStatus || ""}`;

    RestAPIService.getAll(Serviceurls.AYURVEDA_COUNSELLOR_APPROVAL_LIST + qs)
      .then((res) => {
        if (res.data.status === "success") {
          const rows = (res.data.data || []).map((row, i) => ({
            ...row,
            days: row.days ?? "",
          }));
          setAppointments(rows);
          setStatusCount(res.data);
        } else {
          setErrorMsg(res.data.message);
        }
      })
      .catch((e) => setErrorMsg(e.message))
      .finally(() => setIsLoading(false));
  }, [selectedStatus, filterDate]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  /* ----------------------------- DAYS EDIT ----------------------------- */
  const onDaysChange = (row, value) => {
    if (value === "" || /^[0-9]+$/.test(value)) {
      setAppointments((prev) =>
        prev.map((r) =>
          r.appointment_id === row.appointment_id
            ? { ...r, days: value }
            : r
        )
      );
    }
  };

  /* ----------------------------- APPROVE ----------------------------- */
  const onApprove = (row, status) => {
    setIsLoading(true);

    const payload = {
      id: row?.id,
      status: status,
      days: Number(row?.days),
      cancelled_reason: "",
    };

    RestAPIService.create(payload, Serviceurls.AYURVEDA_COUNSELLOR_APPROVAL_LIST)
      .then((res) => {
        if (res.data.status === "success") {
          setSuccessMsg(res.data.message || "Approved successfully");
          fetchAppointments();
        } else {
          setErrorMsg(res.data.message);
        }
      })
      .catch((e) => setErrorMsg(e.message))
      .finally(() => setIsLoading(false));
  };

  /* ----------------------------- CANCEL ----------------------------- */
  const confirmCancel = () => {
    if (!cancelDialog) return;

    setIsLoading(true);

    const payload = {
      appointment_id: cancelDialog.appointment_id,
      status: "Cancelled",
    };

    RestAPIService.updateWithOutId(payload, Serviceurls.AYURVEDA_COUNSELLOR)
      .then((res) => {
        if (res.data.status === "success") {
          setSuccessMsg(res.data.message || "Cancelled successfully");
          fetchAppointments();
        } else {
          setErrorMsg(res.data.message);
        }
      })
      .catch((e) => setErrorMsg(e.message))
      .finally(() => {
        setIsLoading(false);
        setCancelDialog(null);
      });
  };

  /* -------------------------------- COLUMNS -------------------------------- */
  const columns = [
    {
      field: "patient",
      headerName: "Patient’s Details",
      flex: 0.25,
      renderCell: (p) => <CommonPatientDetails data={p.row} />,
    },
    {
      field: "doctor_name",
      headerName: "Doctor Name",
      flex: 0.15,
    },
    {
      field: "start_date",
      headerName: "Start Date",
      flex: 0.15,
      renderCell: (p) =>
        p.row.start_date ? p.row.start_date : "-",
    },
    {
      field: "op_days",
      headerName: "No Of Days",
      flex: 0.15,
      renderCell: (p) =>
        p.row.recommendation_type === "WALKIN" ? p.row?.op_days : p.row?.ip_days,
    },
    {
      field: "days",
      headerName: "Updated Days",
      flex: 0.15,
      renderCell: (p) => (
        <TextField
          size="small"
          value={p.row.days}
          disabled={["Approved", "Cancelled"].includes(p.row.status)}
          onChange={(e) => onDaysChange(p.row, e.target.value)}
          inputProps={{ maxLength: 3, style: { width: "5vw" } }}
        />
      ),
    },
    {
      field: "recommendation_type",
      headerName: "Recommendation Type",
      flex: 0.15,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.15,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => {
        const disabled = ["Approved", "Cancelled"].includes(p.row.status);
        const occupyBox = (IconComponent, color = "error") => (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ width: "3vw", height: "3vw" }}>
            <IconComponent color={color} sx={{ fontSize: "1.8vw" }} />
          </Box>
        );
        return disabled ?
          occupyBox(p.row.status === "Cancelled" ? CancelIcon : DoneOutlineIcon, p.row.status === "Cancelled" ? "error" : "primary") :
          <Box display="flex" gap={1}>
            {renderIconButton(
              "Cancel",
              ImagePaths.CancelRed.default,
              () => setCancelDialog(p.row),
              disabled,
              "1.6vw"
            )}
            {renderIconButton(
              "Approve",
              ImagePaths.TickEnable.default,
              () => onApprove(p.row, 'Approved'),
              disabled,
              "1.6vw"
            )}
          </Box>
      },
    },
  ];

  /* -------------------------------- UI -------------------------------- */
  return (
    <Box sx={{ height: "85vh", p: 1, backgroundColor: Colors.Background }}>
      <div style={{ display: "flex", justifyContent: "end" }}>
        <div className="eMed_Header_OP_home_div">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={filterDate ? filterDate : null}
              name={"datePicker"}
              inputFormat="DD-MM-YYYY"
              views={["year", "month", "day"]}
              onChange={(newDate) => {
                setFilterDate(newDate);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={"Date"}
                  variant="outlined"
                  onKeyDown={(e) => e.preventDefault()}
                  sx={{ width: "15dvw", marginLeft: "1vw" }}
                  size="small"
                />
              )}
            />
          </LocalizationProvider>
        </div>
      </div>
      <Box display="flex" justifyContent="end" gap={1}>
        <AppointmentCard
          label="All"
          color={"#0461B7"}
          count={statusCount.total_count || 0}
          onClick={() => setSelectedStatus("")}
          isSelectedCard={!selectedStatus}
        />
        <AppointmentCard
          label="Pending"
          color={"#ee9b00"}
          count={statusCount.total_pending_count || 0}
          onClick={() => setSelectedStatus("Pending")}
          isSelectedCard={selectedStatus === "Pending"}
        />
        <AppointmentCard
          label="Approved"
          color={"#04B7B1"}
          count={statusCount.total_approved_count || 0}
          onClick={() => setSelectedStatus("Approved")}
          isSelectedCard={selectedStatus === "Approved"}
        />
        <AppointmentCard
          label="Cancelled"
          color={"#B8170D"}
          count={statusCount.total_cancelled_count || 0}
          onClick={() => setSelectedStatus("Cancelled")}
          isSelectedCard={selectedStatus === "Cancelled"}
        />
      </Box>

      <Box sx={{ height: "65vh", background: "#fff", mt: 1 }}>
        <DataGrid
          rows={appointments}
          columns={columns}
          getRowId={(r) => r.appointment_id}
          rowHeight={60}
          headerHeight={40}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Cancel Dialog */}
      <Dialog open={!!cancelDialog} onClose={() => setCancelDialog(null)}>
        <DialogContent>
          <Typography>Are you sure you want to cancel?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(null)}>Close</Button>
          <Button color="error" variant="contained" onClick={() => onApprove(cancelDialog, "Cancel")}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {errorMsg && <ToastMsg severity="error" msg={errorMsg} msgPop={() => setErrorMsg("")} />}
      {successMsg && <ToastMsg severity="success" msg={successMsg} msgPop={() => setSuccessMsg("")} />}
      {isLoading && <Loader />}
    </Box>
  );
};

export default DocApprovalList;
